import { useEffect, useMemo, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { Sparkles } from 'lucide-react';
import { AI } from '../../lib/ai.js';

function shuffleArr(arr) {
  const a = arr.slice();
  for (let i = a.length - 1; i > 0; i--) {
    const j = Math.floor(Math.random() * (i + 1));
    [a[i], a[j]] = [a[j], a[i]];
  }
  return a;
}

function loadJSON(key) {
  try { return JSON.parse(localStorage.getItem(key) || 'null'); } catch { return null; }
}
function saveJSON(key, val) {
  try { localStorage.setItem(key, JSON.stringify(val)); } catch { /* ignore */ }
}
function clearKey(key) {
  try { localStorage.removeItem(key); } catch { /* ignore */ }
}

/**
 * Generic timed exam runner. Manages: shuffled question + option order, per-question
 * answer state, flagging, navigator, timer, submit/grade, domain breakdown, review.
 *
 * Props:
 *   questions: [{ id, domain, q, options[], correct, explanation }]
 *   domainNames: { D1: '...', ... }   (used in domain breakdown headings)
 *   totalSeconds: number               (timer)
 *   passPct: number                    (passing threshold)
 *   stateKey: string                   (localStorage key for resume)
 *   briefingItems: [{label,value}]     (start screen brief grid)
 *   rules: [string]                    (start screen rules list)
 *   tip: string                        (start screen operator tip)
 *   sectionTitle: string               (results review section)
 *   onPickAnother?: () => void         (optional, e.g. objective tests picker)
 *   pickAnotherLabel?: string
 */
export default function ExamRunner({
  questions,
  domainNames = {},
  totalSeconds,
  passPct = 80,
  stateKey,
  briefingItems = [],
  rules = [],
  tip = '',
  startTitle = 'Pre-Mission Briefing',
  startTag = 'Read before launch',
  onPickAnother,
  pickAnotherLabel = '← Pick another',
  examName = 'this exam',
}) {
  const QUESTION_BY_ID = useMemo(() => {
    const map = {};
    questions.forEach((q) => { map[q.id] = q; });
    return map;
  }, [questions]);

  const [phase, setPhase] = useState('start'); // start | exam | results
  const [state, setState] = useState(null);
  const [filter, setFilter] = useState('all'); // for results review
  const [, force] = useState(0);
  const timerRef = useRef(null);

  // AI explanation state — keyed by question ID
  // explainState[qId] = { text: string, status: 'idle'|'streaming'|'done'|'error', error?: string }
  const [explainState, setExplainState] = useState({});
  const explainCacheKey = (qId) => `${stateKey}.explain.${qId}`;

  const persist = (next) => { setState(next); saveJSON(stateKey, next); };

  // initial state load + resume detection
  const [hasResumable, setHasResumable] = useState(false);
  useEffect(() => {
    const saved = loadJSON(stateKey);
    setHasResumable(!!(saved && !saved.finished));
  }, [stateKey]);

  function startExam(resume) {
    if (!resume) {
      const order = shuffleArr(questions.map((q) => q.id));
      const optionOrder = {};
      questions.forEach((q) => {
        optionOrder[q.id] = shuffleArr(q.options.map((_, i) => i));
      });
      const fresh = {
        order, optionOrder,
        answers: {}, flags: {}, currentIdx: 0,
        start: Date.now(),
        deadline: Date.now() + totalSeconds * 1000,
        finished: false,
      };
      persist(fresh);
    } else {
      const saved = loadJSON(stateKey);
      if (!saved) return startExam(false);
      if (!saved.order) {
        saved.order = questions.map((q) => q.id);
        saved.optionOrder = {};
        questions.forEach((q) => { saved.optionOrder[q.id] = q.options.map((_, i) => i); });
      }
      persist(saved);
    }
    setPhase('exam');
    window.scrollTo(0, 0);
  }

  // timer
  useEffect(() => {
    if (phase !== 'exam' || !state) return undefined;
    const tick = () => {
      const remain = Math.max(0, Math.round((state.deadline - Date.now()) / 1000));
      if (remain <= 0) {
        clearInterval(timerRef.current);
        submitExam(true);
      }
      force((n) => n + 1); // re-render timer
    };
    timerRef.current = setInterval(tick, 1000);
    return () => clearInterval(timerRef.current);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [phase, state?.deadline]);

  const remain = state ? Math.max(0, Math.round((state.deadline - Date.now()) / 1000)) : 0;
  const mm = String(Math.floor(remain / 60)).padStart(2, '0');
  const ss = String(remain % 60).padStart(2, '0');
  const timerCls = remain <= 60 ? 'exam-timer exam-timer-critical' : (remain <= 300 ? 'exam-timer exam-timer-low' : 'exam-timer');

  function getDisplayQuestion(idx) {
    if (!state || !state.order) return null;
    const qId = state.order[idx];
    const q = QUESTION_BY_ID[qId];
    if (!q) return null;
    const optMap = (state.optionOrder && state.optionOrder[qId]) || q.options.map((_, i) => i);
    return {
      id: q.id, domain: q.domain, q: q.q,
      options: optMap.map((origIdx) => q.options[origIdx]),
      correctDisplay: optMap.indexOf(q.correct),
      correctOriginal: q.correct,
      optMap,
      explanation: q.explanation,
    };
  }

  function selectAnswer(qId, displayIdx, optMap) {
    const next = { ...state, answers: { ...state.answers, [qId]: optMap[displayIdx] } };
    persist(next);
  }
  function toggleFlag() {
    const qId = state.order[state.currentIdx];
    const next = { ...state, flags: { ...state.flags, [qId]: !state.flags[qId] } };
    persist(next);
  }
  function gotoIdx(i) { persist({ ...state, currentIdx: i }); }
  function nextQ() { if (state.currentIdx < state.order.length - 1) gotoIdx(state.currentIdx + 1); }
  function prevQ() { if (state.currentIdx > 0) gotoIdx(state.currentIdx - 1); }

  function submitExam(timedOut) {
    if (!timedOut) {
      const unanswered = state.order.length - Object.keys(state.answers).length;
      const msg = unanswered > 0
        ? `You have ${unanswered} unanswered question(s). Submit anyway?`
        : 'Submit your exam for grading?';
      if (!confirm(msg)) return;
    }
    if (timerRef.current) clearInterval(timerRef.current);
    const elapsed = Math.min(totalSeconds, Math.round((Date.now() - state.start) / 1000));
    persist({ ...state, finished: true, elapsed, timedOut: !!timedOut });
    setPhase('results');
    window.scrollTo(0, 0);
  }

  function retake() {
    clearKey(stateKey);
    setState(null);
    setHasResumable(false);
    setPhase('start');
    window.scrollTo(0, 0);
  }

  // ---------- AI EXPLAIN (Phase 1) ----------
  function explainQuestion(q, displayOptions, correctDisplay, selDisplay) {
    const qId = q.id;
    // already streaming/done? toggle close on done
    if (explainState[qId]?.status === 'streaming') return;
    if (explainState[qId]?.status === 'done' || explainState[qId]?.status === 'error') {
      setExplainState((s) => { const next = { ...s }; delete next[qId]; return next; });
      return;
    }
    // try cache first
    const cached = loadJSON(explainCacheKey(qId));
    if (cached && typeof cached.text === 'string' && cached.text.length > 0) {
      setExplainState((s) => ({ ...s, [qId]: { text: cached.text, status: 'done' } }));
      return;
    }
    if (!AI.isConfigured()) {
      setExplainState((s) => ({ ...s, [qId]: { text: '', status: 'error', error: 'unconfigured' } }));
      return;
    }
    const correctLetter = String.fromCharCode(65 + correctDisplay);
    const userLetter = selDisplay >= 0 ? String.fromCharCode(65 + selDisplay) : null;
    const optionsBlock = displayOptions
      .map((opt, i) => `${String.fromCharCode(65 + i)}. ${opt}`)
      .join('\n');
    const userAnswerLine = userLetter
      ? `User chose: ${userLetter}. ${displayOptions[selDisplay]}`
      : 'User skipped this question (no answer selected).';

    const system = `You are an exam tutor for ${examName}. Explain in 3 short paragraphs:
1. Why the correct answer is correct (cite the underlying concept).
2. Why the user's chosen answer is wrong (name the misconception). If they skipped, explain the most common wrong pick.
3. One-line memory hook to avoid this trap next time.
Use plain text. No markdown. Under 200 words.`;

    const prompt = `Question: ${q.q}\n\nOptions:\n${optionsBlock}\n\nCorrect: ${correctLetter}. ${displayOptions[correctDisplay]}\n${userAnswerLine}`;

    setExplainState((s) => ({ ...s, [qId]: { text: '', status: 'streaming' } }));
    let latest = '';
    AI.streamInto(
      (text) => {
        latest = text;
        setExplainState((s) => ({ ...s, [qId]: { text, status: 'streaming' } }));
      },
      {
        system,
        prompt,
        onDone: () => {
          const finalText = latest;
          setExplainState((s) => ({ ...s, [qId]: { text: finalText, status: 'done' } }));
          if (finalText) saveJSON(explainCacheKey(qId), { text: finalText, ts: Date.now() });
        },
        onError: (err) => {
          setExplainState((s) => ({
            ...s,
            [qId]: { text: '', status: 'error', error: err?.message || 'AI request failed' },
          }));
        },
      }
    );
  }

  // keyboard shortcuts during exam
  useEffect(() => {
    if (phase !== 'exam') return undefined;
    const handler = (e) => {
      if (e.target.matches?.('input, textarea, select')) return;
      if (e.key === 'ArrowRight') { e.preventDefault(); nextQ(); }
      else if (e.key === 'ArrowLeft') { e.preventDefault(); prevQ(); }
      else if (e.key === 'f' || e.key === 'F') { toggleFlag(); }
      else if (e.key >= '1' && e.key <= '4') {
        const dq = getDisplayQuestion(state.currentIdx);
        if (dq && dq.options[Number(e.key) - 1] !== undefined) {
          selectAnswer(dq.id, Number(e.key) - 1, dq.optMap);
        }
      }
    };
    document.addEventListener('keydown', handler);
    return () => document.removeEventListener('keydown', handler);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [phase, state]);

  // ---------- START ----------
  if (phase === 'start') {
    return (
      <>
        <section>
          <div className="section-head">
            <h2>{startTitle}</h2>
            <span className="section-tag">{startTag}</span>
          </div>

          {briefingItems.length > 0 && (
            <div className="exam-brief">
              <div className="exam-brief-grid">
                {briefingItems.map((b) => (
                  <div key={b.label}>
                    <div className="brief-label">{b.label}</div>
                    <div className="brief-value">{b.value}</div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {rules.length > 0 && (
            <>
              <h3 style={{ marginTop: '2rem' }}>Rules of engagement</h3>
              <ul className="feature-list">
                {rules.map((r, i) => (
                  <li key={i} dangerouslySetInnerHTML={{ __html: r }} />
                ))}
              </ul>
            </>
          )}

          {tip && (
            <div className="callout">
              <strong>Operator Tip</strong>
              {tip}
            </div>
          )}

          <div className="hero-cta" style={{ marginTop: '1.5rem' }}>
            <button type="button" className="btn" onClick={() => startExam(false)}>▶ Begin Exam</button>
            {hasResumable && (
              <button type="button" className="btn btn-ghost" onClick={() => startExam(true)}>↻ Resume saved attempt</button>
            )}
            {onPickAnother && (
              <button type="button" className="btn btn-ghost" onClick={onPickAnother}>{pickAnotherLabel}</button>
            )}
          </div>
        </section>
      </>
    );
  }

  // ---------- EXAM ----------
  if (phase === 'exam' && state) {
    const dq = getDisplayQuestion(state.currentIdx);
    const answered = Object.keys(state.answers).length;
    const flagged = Object.keys(state.flags).filter((k) => state.flags[k]).length;
    const selectedOriginal = dq ? state.answers[dq.id] : undefined;
    const selectedDisplay = (dq && selectedOriginal !== undefined) ? dq.optMap.indexOf(selectedOriginal) : -1;

    return (
      <section>
        <div className="exam-header">
          <div className="exam-status">
            <span className="exam-status-label">QUESTION</span>
            <span className="exam-status-val"><strong>{state.currentIdx + 1}</strong> / <span>{state.order.length}</span></span>
          </div>
          <div className="exam-status">
            <span className="exam-status-label">ANSWERED</span>
            <span className="exam-status-val"><strong>{answered}</strong></span>
          </div>
          <div className="exam-status">
            <span className="exam-status-label">FLAGGED</span>
            <span className="exam-status-val"><strong>{flagged}</strong></span>
          </div>
          <div className="exam-status exam-timer-block">
            <span className="exam-status-label">TIME REMAINING</span>
            <span className={timerCls}>{mm}:{ss}</span>
          </div>
        </div>

        <div className="exam-body">
          <div className="exam-question">
            <div className="q-meta">
              <span className="q-domain">{dq?.domain}</span>
              <button
                type="button"
                className={`q-flag-btn${state.flags[dq?.id] ? ' flagged' : ''}`}
                onClick={toggleFlag}
              >
                {state.flags[dq?.id] ? '⚑ Flagged' : '⚑ Flag'}
              </button>
            </div>
            <h3 className="q-text">{dq?.q}</h3>
            <div className="q-options">
              {dq?.options.map((opt, i) => (
                <label
                  key={i}
                  className={`q-option${selectedDisplay === i ? ' selected' : ''}`}
                >
                  <input
                    type="radio"
                    name={`q-${dq.id}`}
                    value={i}
                    checked={selectedDisplay === i}
                    onChange={() => selectAnswer(dq.id, i, dq.optMap)}
                  />
                  <span className="q-option-letter">{String.fromCharCode(65 + i)}</span>
                  <span className="q-option-text">{opt}</span>
                </label>
              ))}
            </div>

            <div className="q-controls">
              <button type="button" className="btn-mini" onClick={prevQ}>← Prev</button>
              <button type="button" className="btn-mini" onClick={nextQ}>Next →</button>
              <button type="button" className="btn" style={{ marginLeft: 'auto' }} onClick={() => submitExam(false)}>Submit Exam</button>
            </div>
          </div>

          <aside className="exam-nav-panel">
            <div className="nav-panel-head">QUESTION NAVIGATOR</div>
            <div className="nav-grid">
              {state.order.map((qId, i) => {
                const cls = ['nav-cell'];
                if (i === state.currentIdx) cls.push('current');
                if (Object.prototype.hasOwnProperty.call(state.answers, qId)) cls.push('answered');
                if (state.flags[qId]) cls.push('flagged');
                return (
                  <button key={qId} type="button" className={cls.join(' ')} onClick={() => gotoIdx(i)}>
                    {i + 1}
                  </button>
                );
              })}
            </div>
            <div className="nav-legend">
              <span><span className="legend-box leg-current"></span>Current</span>
              <span><span className="legend-box leg-answered"></span>Answered</span>
              <span><span className="legend-box leg-flagged"></span>Flagged</span>
              <span><span className="legend-box leg-blank"></span>Unanswered</span>
            </div>
          </aside>
        </div>
      </section>
    );
  }

  // ---------- RESULTS ----------
  if (phase === 'results' && state) {
    let correct = 0;
    const domainTotals = {};
    questions.forEach((q) => {
      const sel = state.answers[q.id];
      const isCorrect = sel === q.correct;
      if (isCorrect) correct++;
      if (!domainTotals[q.domain]) domainTotals[q.domain] = { total: 0, correct: 0 };
      domainTotals[q.domain].total++;
      if (isCorrect) domainTotals[q.domain].correct++;
    });
    const total = questions.length;
    const pct = Math.round((correct / total) * 100);
    const passed = pct >= passPct;
    const m = String(Math.floor((state.elapsed || 0) / 60)).padStart(2, '0');
    const s = String((state.elapsed || 0) % 60).padStart(2, '0');

    const reviewItems = state.order
      .map((qId) => {
        const q = QUESTION_BY_ID[qId];
        const optMap = (state.optionOrder && state.optionOrder[qId]) || q.options.map((_, i) => i);
        const displayOptions = optMap.map((origIdx) => q.options[origIdx]);
        const correctDisplay = optMap.indexOf(q.correct);
        const sel = state.answers[q.id];
        const selDisplay = sel !== undefined ? optMap.indexOf(sel) : -1;
        const wasAnswered = sel !== undefined;
        const isCorrect = sel === q.correct;
        return { q, displayOptions, correctDisplay, sel, selDisplay, wasAnswered, isCorrect };
      })
      .filter((it) => {
        if (filter === 'wrong') return !it.isCorrect;
        if (filter === 'flagged') return !!state.flags[it.q.id];
        return true;
      });

    return (
      <section>
        <div className={`result-banner${passed ? ' pass' : ' fail'}`}>
          <div className="result-status">{passed ? 'PASS' : 'FAIL'}</div>
          <div className="result-score">
            <span className="score-num">{pct}</span><span className="score-pct">%</span>
          </div>
          <div className="result-meta">
            <span><strong>{correct}</strong> correct of <strong>{total}</strong></span>
            <span>·</span>
            <span>Time: <strong>{m}:{s}</strong></span>
            <span>·</span>
            <span>Threshold: <strong>{passPct}%</strong></span>
          </div>
        </div>

        <div className="section-head" style={{ marginTop: '2.5rem' }}>
          <h2>Domain Breakdown</h2>
          <span className="section-tag">performance per domain</span>
        </div>
        <div className="grid grid-3">
          {Object.keys(domainTotals).sort().map((d) => {
            const dt = domainTotals[d];
            const dpct = Math.round((dt.correct / dt.total) * 100);
            const pcl = dpct >= passPct ? 'jade' : (dpct >= 60 ? 'amber' : 'crimson');
            return (
              <div key={d} className="card">
                <div className="card-num">
                  <span>{d}</span>
                  <span className="weight" style={{ color: `var(--${pcl})` }}>{dpct}%</span>
                </div>
                <h3>{domainNames[d] || d}</h3>
                <p>{dt.correct} of {dt.total} correct</p>
              </div>
            );
          })}
        </div>

        <div className="section-head" style={{ marginTop: '2.5rem' }}>
          <h2>Full Review</h2>
          <span className="section-tag">every question · explanations</span>
        </div>

        <div className="toolbar">
          <button type="button" className={`btn-mini${filter === 'all' ? ' filter-active' : ''}`} onClick={() => setFilter('all')}>All</button>
          <button type="button" className={`btn-mini${filter === 'wrong' ? ' filter-active' : ''}`} onClick={() => setFilter('wrong')}>Wrong only</button>
          <button type="button" className={`btn-mini${filter === 'flagged' ? ' filter-active' : ''}`} onClick={() => setFilter('flagged')}>Flagged</button>
        </div>

        <div className="review-list">
          {reviewItems.length === 0 ? (
            <div className="callout">No questions match this filter.</div>
          ) : reviewItems.map(({ q, displayOptions, correctDisplay, selDisplay, wasAnswered, isCorrect }) => {
            const ex = explainState[q.id];
            const isOpen = !!ex && (ex.status === 'streaming' || ex.status === 'done' || ex.status === 'error');
            const showExplainBtn = !isCorrect; // only on missed/skipped questions
            return (
            <div key={q.id} className={`review-item ${isCorrect ? 'correct' : 'wrong'}`}>
              <div className="review-head">
                <span className="review-num">Q{q.id}</span>
                <span className="review-domain">{q.domain}</span>
                {state.flags[q.id] && <span className="review-flag">⚑ flagged</span>}
                <span className={`review-verdict ${isCorrect ? 'ok' : 'no'}`}>
                  {isCorrect ? '✓ correct' : (wasAnswered ? '✗ wrong' : '— skipped')}
                </span>
              </div>
              <div className="review-q">{q.q}</div>
              <div className="review-options">
                {displayOptions.map((opt, i) => {
                  const cls = ['review-opt'];
                  if (i === correctDisplay) cls.push('is-correct');
                  if (i === selDisplay && !isCorrect) cls.push('is-selected-wrong');
                  if (i === selDisplay && isCorrect) cls.push('is-selected-right');
                  return (
                    <div key={i} className={cls.join(' ')}>
                      <span className="review-opt-letter">{String.fromCharCode(65 + i)}</span>
                      {opt}
                    </div>
                  );
                })}
              </div>
              <div className="review-explain"><strong>Explanation:</strong> {q.explanation}</div>

              {showExplainBtn && (
                <div className="ai-explain">
                  <button
                    type="button"
                    className={`ai-explain__btn${isOpen ? ' ai-explain__btn--open' : ''}`}
                    onClick={() => explainQuestion(q, displayOptions, correctDisplay, selDisplay)}
                    disabled={ex?.status === 'streaming'}
                  >
                    <Sparkles size={14} strokeWidth={2} />
                    {ex?.status === 'streaming' ? 'EXPLAINING…'
                      : ex?.status === 'done' ? 'HIDE AI EXPLANATION'
                      : ex?.status === 'error' ? 'RETRY AI EXPLANATION'
                      : 'AI EXPLAIN'}
                  </button>
                  {isOpen && ex?.status === 'error' && ex.error === 'unconfigured' && (
                    <div className="ai-explain__body ai-explain__body--err">
                      AI is not configured. <Link to="/ai-settings">CONFIGURE AI →</Link>
                    </div>
                  )}
                  {isOpen && ex?.status === 'error' && ex.error !== 'unconfigured' && (
                    <div className="ai-explain__body ai-explain__body--err">
                      {ex.error}
                    </div>
                  )}
                  {isOpen && (ex.status === 'streaming' || ex.status === 'done') && (
                    <div className="ai-explain__body">
                      {ex.text}
                      {ex.status === 'streaming' && <span className="ai-bubble__cursor" aria-hidden="true" />}
                    </div>
                  )}
                </div>
              )}
            </div>
            );
          })}
        </div>

        <div className="hero-cta" style={{ marginTop: '2rem' }}>
          <button type="button" className="btn" onClick={retake}>↻ Retake exam</button>
          {onPickAnother && (
            <button type="button" className="btn btn-ghost" onClick={onPickAnother}>{pickAnotherLabel}</button>
          )}
        </div>
      </section>
    );
  }

  return null;
}
